import React, { useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import HeaderWithBack from '../../../components/Header/headerWithBack';
import CustomPressable from '../../../components/customPressable/customPressable';
import FetchingDataScreen from '../../../components/fetchingDataScreen';
import InfoPopupModal from '../../../components/infoPopupModal';
import { useCssStyle } from '../../../customHook/getCssStyle';
import { celsiusToFahrenheit, fahrenheitToCelsius, sendAcCommand} from '../../../Redux/Actions/roomControlAction';
import { setRenderFlashMessage } from '../../../Redux/Actions/appConfigAction';

const RoomTemperatureControl = ({ rooms = [], configs, onAction, onClose }) => {
  const getStyles = useCssStyle();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isLutron = useSelector(
    (state) =>
      state.deviceConfigSlice?.webAppWidgetsInfo?.appConfigs
        ?.roomControlInterface === 'LUTRON'
  );

  const [activeRoomIndex, setActiveRoomIndex] = useState(0);

  const activeRoom = rooms[activeRoomIndex];
  const hvacList = Array.isArray(activeRoom?.hvac) ? activeRoom.hvac : [];
  const hvac = hvacList[0];

  // ─── Per-room state ────────────────────────────────────────────────────────
  // Each room keeps its own acState + activeFanSpeed so that locally-made
  // changes (power, fan speed) survive when the user switches rooms and returns.
  const readHvacState = (hvacData) => ({
    temperature: Number(hvacData?.temperatureValue?.slice(2, 4) ?? 20),
    temperatureUnit: hvacData?.temperatureUnit ?? 'C',
    powerSwitch: hvacData?.mainSwitch ?? 'OFF',
    currentRoomTemperature: hvacData?.currentRoomTemperature?.slice(2, 4) ?? '',
  });

  const buildInitialRoomStates = () => {
    const init = {};
    rooms.forEach((room, idx) => {
      const h = Array.isArray(room.hvac) ? room.hvac[0] : null;
      init[idx] = {
        acState: readHvacState(h),
        activeFanSpeed: h?.fanSpeed ?? null,
      };
    });
    return init;
  };

  const [roomStates, setRoomStates] = useState(buildInitialRoomStates);

  const acState = roomStates[activeRoomIndex]?.acState ?? readHvacState(hvac);
  const activeFanSpeed = roomStates[activeRoomIndex]?.activeFanSpeed ?? hvac?.fanSpeed ?? null;

  const setAcState = (updater) => {
    setRoomStates((prev) => {
      const current = prev[activeRoomIndex] ?? { acState: readHvacState(hvac), activeFanSpeed: null };
      return {
        ...prev,
        [activeRoomIndex]: {
          ...current,
          acState: typeof updater === 'function' ? updater(current.acState) : updater,
        },
      };
    });
  };

  const setActiveFanSpeed = (speed) => {
    setRoomStates((prev) => {
      const current = prev[activeRoomIndex] ?? { acState: readHvacState(hvac), activeFanSpeed: null };
      return {
        ...prev,
        [activeRoomIndex]: { ...current, activeFanSpeed: speed },
      };
    });
  };

  const [loader, setLoader] = useState({ loading: false, message: '' });
  const [fetchApiError, setFetchApiError] = useState({
    errorStatus: false,
    errorMessage: '',
  });

  const updateLoader = (loading, message = '') =>
    setLoader({ loading: !!loading, message });

  // ─── Temperature limits ────────────────────────────────────────────────────
  const minTemp =
    acState.temperatureUnit === 'F'
      ? Number(configs?.farenheitMin ?? 60)
      : Number(configs?.celciusMin ?? 16);
  const maxTemp =
    acState.temperatureUnit === 'F'
      ? Number(configs?.farenheitMax ?? 86)
      : Number(configs?.celciusMax ?? 30);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleTemperatureStep = (delta) => {
    if (!hvac) return;
    const newTemp = Math.min(
      maxTemp,
      Math.max(minTemp, Number(acState.temperature) + delta)
    );
    const prevTemp = acState.temperature;
    setAcState((prev) => ({ ...prev, temperature: newTemp }));
    updateLoader(true, 'Sending request...');
    dispatch(
      sendAcCommand(
        'setTempOfAC',
        newTemp,
        `+${acState.temperatureUnit}`,
        hvac.HVACIdentifierKey,
        (status, message) => {
          updateLoader(false);
          dispatch(
            setRenderFlashMessage(true, { type: status ? 'success' : 'danger', message })
          );
          if (!status) setAcState((prev) => ({ ...prev, temperature: prevTemp }));
        }
      )
    );
  };

  const handleACPowerSwitch = (targetState) => {
    if (!hvac) return;
    const prevPower = acState.powerSwitch;
    // Optimistic update
    setAcState((prev) => ({ ...prev, powerSwitch: targetState }));
    updateLoader(true, 'Sending request...');
    dispatch(
      sendAcCommand('toggleAC', null, null, hvac.HVACIdentifierKey, (status, message) => {
        updateLoader(false);
        dispatch(
          setRenderFlashMessage(true, { type: status ? 'success' : 'danger', message })
        );
        // Revert on failure
        if (!status) setAcState((prev) => ({ ...prev, powerSwitch: prevPower }));
      })
    );
  };

  const handleFanSpeed = (fan) => {
    setActiveFanSpeed(getFanSpeedCode(fan));
    onAction(
      fan.action,
      isLutron ? { interface: 'lutron', action: fan.action } : {},
      'fanSpeed'
    );
  };

  const handleAcUnitSwitch = (changedUnit) => {
    if (!configs?.acUnitChangeViaServer || !hvac) return;
    const prevUnit = acState.temperatureUnit;
    updateLoader(true, 'Sending request...');
    const action =
      changedUnit === 'F'
        ? 'setDisplayTempUnitToCelcius'
        : 'setDisplayTempUnitToFahrenheit';
    dispatch(
      sendAcCommand(
        action,
        acState.temperature,
        acState.temperatureUnit,
        hvac.HVACIdentifierKey,
        (status, message) => {
          updateLoader(false);
          dispatch(
            setRenderFlashMessage(true, { type: status ? 'success' : 'danger', message })
          );
          if (status) {
            setAcState((prev) => ({
              ...prev,
              temperatureUnit: changedUnit,
              temperature:
                changedUnit === 'F'
                  ? celsiusToFahrenheit(
                      prev.temperature,
                      Number(configs?.farenheitMin),
                      Number(configs?.farenheitMax)
                    )
                  : fahrenheitToCelsius(
                      prev.temperature,
                      Number(configs?.celciusMin),
                      Number(configs?.celciusMax)
                    ),
            }));
          } else {
            setAcState((prev) => ({ ...prev, temperatureUnit: prevUnit }));
          }
        }
      )
    );
  };

  const hasFanOptions =
    Array.isArray(configs?.fanSpeedConfig) && configs.fanSpeedConfig.length > 0;
  const getFanSpeedCode = (fan) => {
    const suffix = fan.action.replace('setFanSpeedTo', '');
    return 'FAN_SPEED_' + suffix
      .replace(/([A-Z])/g, '_$1')
      .toUpperCase()
      .replace(/^_/, '');
  };

  const isOn = acState.powerSwitch === 'ON';
  const isCelsius = acState.temperatureUnit === 'C';

  // ─── No data guard ─────────────────────────────────────────────────────────
  if (!hvac) {
    return (
      <View style={getStyles(['roomTemperatureFullScreenContainer'])}>
        {onClose && <HeaderWithBack onBack={onClose} />}
        <View style={getStyles(['roomTemperatureNoDataContainer'])}>
          <Text style={getStyles(['roomTemperatureNoDataText'])}>No data available temperature control</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={getStyles(['roomTemperatureFullScreenContainer'])}>

      {/* ── Header (modal / overlay mode uses back to close) ─────────────── */}
      {onClose && <HeaderWithBack handleCustomBackPress={onClose} />}

      <ScrollView
        contentContainerStyle={getStyles([
          'roomTemperatureScrollContent',
          onClose ? 'roomTemperatureScrollContentWithClose' : '',
        ])}
        id='roomTemperatureScrollContent'
        showsVerticalScrollIndicator={false}
      >

        {/* ── Heading ──────────────────────────────────────────────────────── */}
        <Text style={getStyles(['roomTemperatureHeading'])} id='roomTemperatureHeading'>Temperature</Text>

        {/* ── UNIT toggle + FAN MODE pills ─────────────────────────────────── */}
        <View style={getStyles(['roomTemperatureControlsRow'])} id='roomTemperatureControlsRow'>

          {/* UNIT pill */}
          <View style={getStyles(['roomTemperatureUnitContainer'])} id='roomTemperatureUnitContainer'>
            <View style={getStyles(['roomTemperatureUnitPill'])} id='roomTemperatureUnitPill'>
              <CustomPressable
                onPress={() => handleAcUnitSwitch('C')}
                disabled={isCelsius}
                style={getStyles([
                  'roomTemperatureUnitButton',
                  isCelsius ? 'roomTemperatureUnitButtonActive' : '',
                ])}
                id="roomTemperatureUnitButton roomTemperatureUnitButtonActive"
              >
                <Text
                  style={getStyles([
                    'roomTemperatureUnitButtonText',
                    isCelsius ? 'roomTemperatureUnitButtonTextActive' : '',
                  ])}
                  id='roomTemperatureUnitButtonText roomTemperatureUnitButtonTextActive'
                >
                  C
                </Text>
              </CustomPressable>

              <CustomPressable
                onPress={() => handleAcUnitSwitch('F')}
                disabled={!isCelsius}
                style={getStyles([
                  'roomTemperatureUnitButton',
                  !isCelsius ? 'roomTemperatureUnitButtonActive' : '',
                ])}
                id="roomTemperatureUnitButton roomTemperatureUnitButtonActive"
              >
                <Text
                  style={getStyles([
                    'roomTemperatureUnitButtonText',
                    !isCelsius ? 'roomTemperatureUnitButtonTextActive' : '',
                  ])}
                  id='roomTemperatureUnitButtonText roomTemperatureUnitButtonTextActive'
                >
                  F
                </Text>
              </CustomPressable>
            </View>
            <Text style={getStyles(['roomTemperatureControlLabel'])} id='roomTemperatureControlLabel' numberOfLines={1}>
              UNIT
            </Text>
          </View>

          {/* FAN MODE */}
          {hasFanOptions && (
            <View style={getStyles(['roomTemperatureFanModeContainer'])} id='roomTemperatureFanModeContainer'>
              <View style={getStyles(['roomTemperatureFanOptionsRow'])} id='roomTemperatureFanOptionsRow'>
                {configs.fanSpeedConfig.map((fan) => {
                  const isActive = activeFanSpeed != null && getFanSpeedCode(fan) === activeFanSpeed;
                  return (
                    <CustomPressable
                      key={fan.position}
                      onPress={() => handleFanSpeed(fan)}
                      style={getStyles([
                        'roomTemperatureFanOptionButton',
                        isActive ? 'roomTemperatureFanOptionButtonActive' : '',
                      ])}
                      id="roomTemperatureFanOptionButton roomTemperatureFanOptionButtonActive"
                    >
                      <Text
                        style={getStyles([
                          'roomTemperatureFanOptionText',
                          isActive ? 'roomTemperatureFanOptionTextActive' : '',
                        ])}
                        id='roomTemperatureFanOptionText roomTemperatureFanOptionTextActive'
                      >
                        {fan.label}
                      </Text>
                    </CustomPressable>
                  );
                })}
              </View>
              <Text style={getStyles(['roomTemperatureControlLabel'])} id='roomTemperatureControlLabel' numberOfLines={1}>
                FAN MODE
              </Text>
            </View>
          )}
        </View>

        {/* ── Temperature control ───────────────────────────────────────────── */}
        <View style={getStyles(['roomTemperatureTempSection'])} id='roomTemperatureTempSection'>
          <Text style={getStyles(['roomTemperatureSetToLabel'])} id='roomTemperatureSetToLabel'>
            SET TO
          </Text>

          <View style={getStyles(['roomTemperatureTempRow'])} id='roomTemperatureTempRow'>
            {/* Minus */}
            <CustomPressable
              onPress={() => handleTemperatureStep(-1)}
              disabled={Number(acState.temperature) <= minTemp}
              style={getStyles(['roomTemperatureStepButton'])}
              id="roomTemperatureStepButton"
            >
              <Text style={getStyles(['roomTemperatureStepButtonText'])} id='roomTemperatureStepButtonText'>−</Text>
            </CustomPressable>

            {/* Value */}
            <View style={getStyles(['roomTemperatureTempValueContainer'])} id='roomTemperatureTempValueContainer'>
              <Text style={getStyles(['roomTemperatureTempValueText'])} id='roomTemperatureTempValueText'>
                {acState.temperature}°{acState.temperatureUnit}
              </Text>
            </View>

            {/* Plus */}
            <CustomPressable
              onPress={() => handleTemperatureStep(1)}
              disabled={Number(acState.temperature) >= maxTemp}
              style={getStyles(['roomTemperatureStepButton'])}
              id="roomTemperatureStepButton"
            >
              <Text style={getStyles(['roomTemperatureStepButtonText'])} id='roomTemperatureStepButtonText'>+</Text>
            </CustomPressable>
          </View>

          <Text style={getStyles(['roomTemperatureUnitSubLabel'])} id='roomTemperatureUnitSubLabel'>
            {isCelsius ? 'CELSIUS' : 'FAHRENHEIT'}
          </Text>
        </View>

        {/* ── ON / OFF ─────────────────────────────────────────────────────── */}
        {acState.powerSwitch ? (
          <View style={getStyles(['roomTemperaturePowerRow'])} id='roomTemperaturePowerRow'>
            <CustomPressable
              onPress={() => handleACPowerSwitch('ON')}
              disabled={isOn}
              style={getStyles([
                'roomTemperaturePowerButton',
                isOn ? 'roomTemperaturePowerButtonActive' : '',
              ])}
              id="roomTemperaturePowerButton roomTemperaturePowerButtonActive"
            >
              <Text
                style={getStyles([
                  'roomTemperaturePowerButtonText',
                  isOn ? 'roomTemperaturePowerButtonTextActive' : '',
                ])}
                id='roomTemperaturePowerButtonText roomTemperaturePowerButtonTextActive'
              >
                {t('room_control.on') || 'ON'}
              </Text>
            </CustomPressable>

            <CustomPressable
              onPress={() => handleACPowerSwitch('OFF')}
              disabled={!isOn}
              style={getStyles([
                'roomTemperaturePowerButton',
                !isOn ? 'roomTemperaturePowerButtonActive' : '',
              ])}
              id="roomTemperaturePowerButton roomTemperaturePowerButtonActive"
            >
              <Text
                style={getStyles([
                  'roomTemperaturePowerButtonText',
                  !isOn ? 'roomTemperaturePowerButtonTextActive' : '',
                ])}
                id='roomTemperaturePowerButtonText roomTemperaturePowerButtonTextActive'
              >
                {t('room_control.off') || 'OFF'}
              </Text>
            </CustomPressable>
          </View>
        ) : null}
      </ScrollView>

      {/* ── Room tabs ────────────────────────────────────────────────────────── */}
      {rooms.length > 0 && (
        <View style={getStyles(['roomTemperatureRoomTabsWrapper'])} id='roomTemperatureRoomTabsWrapper'>
          <View style={getStyles(['roomTemperatureRoomTabsRow'])} id='roomTemperatureRoomTabsRow'>
            {rooms.map((room, index) => {
              const isActive = activeRoomIndex === index;
              return (
                <CustomPressable
                  key={room.id ?? index}
                  onPress={() => setActiveRoomIndex(index)}
                  style={getStyles([
                    'roomTemperatureRoomTab',
                    isActive ? 'roomTemperatureRoomTabActive' : '',
                  ])}
                  id="roomTemperatureRoomTab roomTemperatureRoomTabActive"
                >
                  <Text
                    style={getStyles([
                      isActive
                        ? 'roomTemperatureRoomTabTextActive'
                        : 'roomTemperatureRoomTabText',
                    ])}
                    id='roomTemperatureRoomTabTextActive roomTemperatureRoomTabText'
                  >
                    {room.label}
                  </Text>
                </CustomPressable>
              );
            })}
          </View>
        </View>
      )}

      {fetchApiError.errorStatus && (
        <InfoPopupModal
          subtitle={fetchApiError.errorMessage}
          onContinue={() =>
            setFetchApiError({ errorStatus: false, errorMessage: '' })
          }
          primaryBtnLabel="Close"
        />
      )}
      {loader.loading && <FetchingDataScreen loaderMessage={loader.message} />}
    </View>
  );
};

export default RoomTemperatureControl;
